/**
 * Authentication Context
 *
 * Kullanıcı authentication state'ini yönetir.
 * Google ve Email/Password girişlerini destekler.
 *
 * Clean Code prensipleri:
 * - Single Responsibility: Sadece auth işlemleri
 * - DRY: Tekrar eden auth logic merkezi
 * - Error Handling: Tüm hatalar yakalanır
 * - Type Safety: Full TypeScript support
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  linkWithPopup,
  linkWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInAnonymously,
} from 'firebase/auth';
import { doc, onSnapshot, updateDoc, serverTimestamp, deleteField, increment, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import {
  getUserProfile,
  createUserProfile,
} from '../services/firestore';
import type { User, AuthState, AuthProvider } from '../types';
import { eventTracker } from '../services/eventTracker';
import { trackSignUp, trackLogin as trackLoginGA4 } from '../services/analytics';
import { metaPixel } from '../services/metaPixel';

// ==================== Context Types ====================

interface AuthContextValue extends AuthState {
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  sendEmailLink: (email: string) => Promise<void>;
  verifyEmailLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  createAnonymousUser: () => Promise<void>; // Lazy initialization için rename
  signInWithApple: () => Promise<void>;
  isGoogleAuthInProgress: boolean; // Google popup açık mı?
}

// ==================== Context Creation ====================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ==================== Helper Functions ====================

/**
 * Firebase Auth error kodlarını Türkçeye çevir
 */
function getFirebaseErrorMessage(error: any): string {
  const code = error?.code || '';

  // Yaygın Firebase Auth hataları
  const errorMessages: Record<string, string> = {
    'auth/popup-closed-by-user': 'Google giriş penceresi kapatıldı',
    'auth/cancelled-popup-request': 'Giriş işlemi iptal edildi',
    'auth/popup-blocked': 'Popup engellenmiş. Lütfen tarayıcınızın popup ayarlarını kontrol edin',
    'auth/credential-already-in-use': 'Bu hesap zaten kullanımda',
    'auth/invalid-email': 'Geçersiz email adresi',
    'auth/user-not-found': 'Kullanıcı bulunamadı',
    'auth/wrong-password': 'Hatalı şifre',
    'auth/email-already-in-use': 'Bu email zaten kullanımda',
    'auth/weak-password': 'Şifre çok zayıf',
    'auth/network-request-failed': 'İnternet bağlantısı hatası',
    'auth/too-many-requests': 'Çok fazla deneme. Lütfen daha sonra tekrar deneyin',
  };

  return errorMessages[code] || error.message || 'Bir hata oluştu';
}

/**
 * Login tracking helper
 * Her başarılı girişte lastLoginAt, loginCount günceller ve event loglar
 * @see USER_BEHAVIOR_ANALYTICS_ROADMAP.md
 */
async function updateLoginTracking(userId: string, method: AuthProvider): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);

    // Mevcut kullanıcı verisini al (streak hesaplama için)
    const userSnap = await getDoc(userRef);
    const userData = userSnap.data();

    // Temel güncellemeler
    const updates: Record<string, any> = {
      lastLoginAt: serverTimestamp(),
      loginCount: increment(1),
    };

    // Streak hesaplama (basit versiyon)
    if (userData?.lastLoginAt) {
      const lastLogin = userData.lastLoginAt.toDate();
      const now = new Date();
      const daysDiff = Math.floor(
        (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysDiff === 1) {
        // Ertesi gün geldi - streak artır
        const newStreak = (userData.currentStreak || 0) + 1;
        updates.currentStreak = newStreak;
        updates.daysActive = increment(1);

        // Longest streak güncelle
        if (newStreak > (userData.longestStreak || 0)) {
          updates.longestStreak = newStreak;
        }

        // Milestone check
        if (newStreak === 3) {
          eventTracker.trackStreak3(userId);
        } else if (newStreak === 7) {
          eventTracker.trackStreak7(userId);
        }
      } else if (daysDiff > 1) {
        // Streak kırıldı - sıfırla
        updates.currentStreak = 1;
        updates.daysActive = increment(1);
      }
      // daysDiff === 0 → Aynı gün, streak değişmez
    } else {
      // İlk login veya lastLoginAt yok
      updates.currentStreak = 1;
      updates.daysActive = 1;
      updates.longestStreak = 1;
    }

    // Firestore güncelle
    await updateDoc(userRef, updates);

    // Event tracker'a logla
    eventTracker.trackLogin(userId, method);

    // GA4 login tracking
    const ga4Method = method === 'apple' ? 'google' : method; // GA4 apple desteklemiyor, google olarak logla
    trackLoginGA4(ga4Method as 'google' | 'email');

    // Meta Pixel login tracking
    metaPixel.trackLogin(method);

    console.log('✅ Login tracking güncellendi:', userId);
  } catch (error) {
    // Silent fail - tracking ana akışı bozmamalı
    console.error('⚠️ Login tracking hatası (ignored):', error);
  }
}

// ==================== Provider Component ====================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isGoogleAuthInProgress, setIsGoogleAuthInProgress] = useState<boolean>(false);

  // Firebase Auth state değişikliklerini dinle
  useEffect(() => {
    let userProfileUnsubscribe: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // Kullanıcı giriş yapmış, Firestore'dan profil bilgisini çek
          let userProfile = await getUserProfile(firebaseUser.uid);

          // Firestore'da profil yoksa oluştur
          if (!userProfile) {
            userProfile = await createUserProfile(
              firebaseUser.uid,
              firebaseUser.email || '',
              firebaseUser.displayName || null,
              firebaseUser.photoURL || null,
              firebaseUser.providerData[0]?.providerId === 'google.com'
                ? 'google'
                : 'email'
            );
          }

          setUser(userProfile);
          setError(null);

          // Meta Pixel Advanced Matching — send user data for better attribution
          metaPixel.setUserData({
            email: firebaseUser.email,
            firstName: firebaseUser.displayName?.split(' ')[0],
            userId: firebaseUser.uid,
          });

          // Real-time user profile listener (limits güncellemelerini dinle)
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          userProfileUnsubscribe = onSnapshot(
            userDocRef,
            (snapshot) => {
              if (snapshot.exists()) {
                const updatedUser = {
                  id: snapshot.id,
                  ...snapshot.data(),
                } as User;
                setUser(updatedUser);
              }
            },
            (error) => {
              console.error('User profile listener error:', error);
            }
          );
        } else {
          // Kullanıcı çıkış yapmış
          setUser(null);
          // Listener'ı temizle
          if (userProfileUnsubscribe) {
            userProfileUnsubscribe();
            userProfileUnsubscribe = null;
          }
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError(err instanceof Error ? err.message : 'Bilinmeyen hata');
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup
    return () => {
      unsubscribe();
      if (userProfileUnsubscribe) {
        userProfileUnsubscribe();
      }
    };
  }, []);

  // ==================== Anonymous Sign In (Lazy - sadece kategori seçilince) ====================

  const createAnonymousUser = async (): Promise<void> => {
    try {
      // Global loading state'i KULLANMA - component kendi loading'ini yönetsin
      setError(null);

      // Zaten kullanıcı varsa (anonymous veya registered), yeni anonymous oluşturma
      if (auth.currentUser) {
        console.log('✅ Kullanıcı zaten var, yeni anonymous oluşturulmadı');
        return;
      }

      // Anonymous giriş yap
      const result = await signInAnonymously(auth);
      const newFirebaseUser = result.user;

      // Anonymous user profili oluştur
      const userProfile = await createUserProfile(
        newFirebaseUser.uid,
        '', // Anonymous kullanıcının email'i yok
        null,
        null,
        'email', // authProvider'ı 'email' olarak set ediyoruz (upgrade için)
        true // isAnonymous = true
      );

      setUser(userProfile);
      console.log('✅ Yeni anonymous kullanıcı oluşturuldu (kategori seçildi)');
    } catch (err) {
      console.error('Anonymous sign in error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Anonymous giriş başarısız';
      setError(errorMessage);
      throw err;
    }
  };

  // ==================== Google Sign In ====================

  const signInWithGoogle = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      setIsGoogleAuthInProgress(true); // Popup açılıyor

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account', // Kullanıcı her seferinde hesap seçebilsin
      });

      // Eğer anonymous kullanıcı varsa, upgrade et
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        const oldUserId = auth.currentUser.uid;
        console.log('🔗 Anonymous kullanıcı yükseltiliyor, UID:', oldUserId);

        try {
          // linkWithPopup ile anonymous hesabı Google hesabına bağla
          // Bu sayede UID DEĞİŞMEZ!
          const result = await linkWithPopup(auth.currentUser, provider);
          const firebaseUser = result.user;

          console.log('✅ UID korundu, yeni Google hesabı bağlandı:', firebaseUser.uid);

          // Firestore'daki user profilini güncelle (anonymous → registered)
          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || null,
            photoURL: firebaseUser.photoURL || null,
            userType: 'registered',
            authProvider: 'google',
            updatedAt: serverTimestamp(),
            // limits'i KALDIR - artık kayıtlı kullanıcı
            limits: deleteField(),
          });

          // User profilini yeniden al
          const userProfile = await getUserProfile(firebaseUser.uid);
          setUser(userProfile);
          await updateLoginTracking(firebaseUser.uid, 'google');

          // Track upgrade as sign up + account upgrade
          trackSignUp('google');
          metaPixel.trackSignUp('google');
          metaPixel.trackAccountUpgrade();

          console.log('✅ Anonymous → Google upgrade başarılı, veriler korundu');
        } catch (linkError: any) {
          console.log('⚠️ Link başarısız:', linkError.code);
          console.log('🔍 Error object:', linkError);
          console.log('🔍 Credential var mı?', !!linkError.credential);
          console.log('🔍 customData:', linkError.customData);

          // Eğer credential zaten kullanımdaysa (auth/credential-already-in-use)
          if (linkError.code === 'auth/credential-already-in-use') {
            console.log('🔄 Bu Google hesabı zaten kayıtlı, credential ile giriş yapılıyor');

            // Firebase error'ında credential var mı kontrol et
            const errorCredential = linkError.credential;
            const tokenResponse = linkError.customData?._tokenResponse;

            console.log('🔍 errorCredential:', errorCredential);
            console.log('🔍 tokenResponse:', tokenResponse);
            console.log('🔍 tokenResponse.oauthIdToken:', tokenResponse?.oauthIdToken);

            let credential = errorCredential;

            // Eğer direkt credential yoksa ama token response varsa, credential oluştur
            // Google OAuth kullanıyor, oauthIdToken field'ını kullan (idToken değil!)
            if (!credential && tokenResponse?.oauthIdToken) {
              console.log('🔧 Token response\'dan credential oluşturuluyor...');
              credential = GoogleAuthProvider.credential(tokenResponse.oauthIdToken);
              console.log('✅ Credential oluşturuldu');
            }

            if (!credential) {
              console.error('❌ Credential bulunamadı, normal popup açılıyor');
              // Fallback: Credential yoksa normal popup aç
              await firebaseSignOut(auth);
              const result = await signInWithPopup(auth, provider);
              const firebaseUser = result.user;
              let userProfile = await getUserProfile(firebaseUser.uid);
              if (!userProfile) {
                userProfile = await createUserProfile(
                  firebaseUser.uid,
                  firebaseUser.email || '',
                  firebaseUser.displayName || null,
                  firebaseUser.photoURL || null,
                  'google'
                );
              }
              setUser(userProfile);
              await updateLoginTracking(firebaseUser.uid, 'google');
              console.log('✅ Google ile giriş başarılı (fallback popup)');
              return;
            }

            // Önce anonymous kullanıcıyı çıkış yaptır
            await firebaseSignOut(auth);

            // Credential ile direkt giriş yap (TEKRAR POPUP YOK!)
            const { signInWithCredential } = await import('firebase/auth');
            const result = await signInWithCredential(auth, credential);
            const firebaseUser = result.user;

            console.log('✅ Credential ile giriş başarılı, UID:', firebaseUser.uid);
            console.log('🔍 firebaseUser.photoURL:', firebaseUser.photoURL);

            // firebaseUser.photoURL bazen boş gelebilir, tokenResponse'dan al
            const photoURL = firebaseUser.photoURL || tokenResponse?.photoUrl || null;
            console.log('🔍 Final photoURL:', photoURL);

            // Firestore'da profil var mı kontrol et
            let userProfile = await getUserProfile(firebaseUser.uid);

            if (!userProfile) {
              // İlk defa giriş yapıyor, profil oluştur
              userProfile = await createUserProfile(
                firebaseUser.uid,
                firebaseUser.email || '',
                firebaseUser.displayName || null,
                photoURL,
                'google'
              );
            } else {
              // Profil var ama anonymous'dan upgrade olmuş olabilir, Google bilgilerini güncelle
              console.log('🔄 Mevcut profil Google bilgileriyle güncelleniyor...');
              await updateDoc(doc(db, 'users', firebaseUser.uid), {
                email: firebaseUser.email || '',
                displayName: firebaseUser.displayName || null,
                photoURL: photoURL,
                authProvider: 'google',
                updatedAt: serverTimestamp(),
                // userType'ı sadece GEREKİRSE güncelle (anonymous -> registered)
                // Mevcut userType ile hedef aynıysa veya target zaten registered/premium ise dokunma
                ...(userProfile.userType === 'anonymous' ? { userType: 'registered' } : {}),
                // limits'i sadece upgradese kaldır
                ...(userProfile.userType === 'anonymous' ? { limits: deleteField() } : {}),
              });

              // Veri birleştirme yapılmadığı için başarı mesajını engelle
              // (Sadece auth/credential-already-in-use durumunda, yani hesap değiştirildiğinde)
              if (typeof window !== 'undefined') {
                window.localStorage.removeItem('hadAnonymousSession');
                window.localStorage.removeItem('unsentMessage');
                window.localStorage.removeItem('unsentConversationId');
              }
              // Güncellenmiş profili al
              userProfile = await getUserProfile(firebaseUser.uid);
            }

            setUser(userProfile);
            await updateLoginTracking(firebaseUser.uid, 'google');
            console.log('✅ Google ile giriş başarılı (tek popup ile)');
          } else if (linkError.code === 'auth/email-already-in-use') {
            // Email zaten kullanımda, normal popup aç
            console.log('🔄 Email zaten kullanımda, normal giriş yapılıyor');
            await firebaseSignOut(auth);
            const result = await signInWithPopup(auth, provider);
            const firebaseUser = result.user;
            let userProfile = await getUserProfile(firebaseUser.uid);
            if (!userProfile) {
              userProfile = await createUserProfile(
                firebaseUser.uid,
                firebaseUser.email || '',
                firebaseUser.displayName || null,
                firebaseUser.photoURL || null,
                'google'
              );
            }
            setUser(userProfile);
            await updateLoginTracking(firebaseUser.uid, 'google');
            console.log('✅ Google ile giriş başarılı (email-already-in-use)');
          } else {
            // Başka bir hata, yukarı fırlat
            throw linkError;
          }
        }
      } else {
        // Normal Google giriş
        const result = await signInWithPopup(auth, provider);
        const firebaseUser = result.user;

        // Firestore'da profil var mı kontrol et
        let userProfile = await getUserProfile(firebaseUser.uid);

        if (!userProfile) {
          // İlk defa giriş yapıyor, profil oluştur
          userProfile = await createUserProfile(
            firebaseUser.uid,
            firebaseUser.email || '',
            firebaseUser.displayName || null,
            firebaseUser.photoURL || null,
            'google'
          );

          // Yeni kayıt — track sign up
          trackSignUp('google');
          metaPixel.trackSignUp('google');
        }

        setUser(userProfile);
        await updateLoginTracking(firebaseUser.uid, 'google');
        console.log('✅ Google ile giriş başarılı');
      }
    } catch (err) {
      console.error('Google sign in error:', err);
      const errorMessage = getFirebaseErrorMessage(err);
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
      setIsGoogleAuthInProgress(false); // Popup kapandı/tamamlandı
    }
  };

  // ==================== Apple Sign In ====================

  const signInWithApple = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      // Apple popup açıldığında state takibi yapmıyoruz çünkü Google gibi özel bir UI durumu yok

      const provider = new OAuthProvider('apple.com');
      // Türkçe dil desteği için
      auth.languageCode = 'tr';

      // Scopes: Email ve Name istiyoruz
      provider.addScope('email');
      provider.addScope('name');

      // Eğer anonymous kullanıcı varsa, upgrade et
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        const oldUserId = auth.currentUser.uid;
        console.log('🔗 Anonymous kullanıcı Apple ile yükseltiliyor, UID:', oldUserId);

        try {
          // linkWithPopup ile anonymous hesabı Apple hesabına bağla
          const result = await linkWithPopup(auth.currentUser, provider);
          const firebaseUser = result.user;

          console.log('✅ UID korundu, yeni Apple hesabı bağlandı:', firebaseUser.uid);

          // Firestore'daki user profilini güncelle
          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || null,
            photoURL: firebaseUser.photoURL || null,
            userType: 'registered',
            authProvider: 'apple',
            updatedAt: serverTimestamp(),
            limits: deleteField(),
          });

          // User profilini yeniden al
          const userProfile = await getUserProfile(firebaseUser.uid);
          setUser(userProfile);
          await updateLoginTracking(firebaseUser.uid, 'apple');

          // Track upgrade as sign up + account upgrade
          trackSignUp('google'); // GA4 apple desteklemiyor
          metaPixel.trackSignUp('apple');
          metaPixel.trackAccountUpgrade();

          console.log('✅ Anonymous → Apple upgrade başarılı, veriler korundu');
        } catch (linkError: any) {
          console.log('⚠️ Link başarısız:', linkError.code);

          if (linkError.code === 'auth/credential-already-in-use') {
            console.log('🔄 Bu Apple hesabı zaten kayıtlı, normal giriş yapılıyor');

            // Anonymous user'dan çık
            await firebaseSignOut(auth);

            // Normal giriş yap
            const result = await signInWithPopup(auth, provider);
            const firebaseUser = result.user;

            // Profil kontrolü
            let userProfile = await getUserProfile(firebaseUser.uid);
            if (!userProfile) {
              userProfile = await createUserProfile(
                firebaseUser.uid,
                firebaseUser.email || '',
                firebaseUser.displayName || null,
                firebaseUser.photoURL || null,
                'apple'
              );
            }
            setUser(userProfile);
            await updateLoginTracking(firebaseUser.uid, 'apple');
            console.log('✅ Apple ile giriş başarılı (eski hesaba geçildi)');
          } else {
            throw linkError;
          }
        }
      } else {
        // Normal Apple giriş (Anonymous değil)
        const result = await signInWithPopup(auth, provider);
        const firebaseUser = result.user;

        let userProfile = await getUserProfile(firebaseUser.uid);
        if (!userProfile) {
          userProfile = await createUserProfile(
            firebaseUser.uid,
            firebaseUser.email || '',
            firebaseUser.displayName || null,
            firebaseUser.photoURL || null,
            'apple'
          );

          // Yeni kayıt — track sign up
          trackSignUp('google'); // GA4 apple desteklemiyor
          metaPixel.trackSignUp('apple');
        }
        setUser(userProfile);
        await updateLoginTracking(firebaseUser.uid, 'apple');
        console.log('✅ Apple ile giriş başarılı');
      }
    } catch (err) {
      console.error('Apple sign in error:', err);
      // Apple hatalarını da handle et
      const errorMessage = getFirebaseErrorMessage(err);
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ==================== Email/Password Sign In ====================

  const signInWithEmail = async (
    email: string,
    password: string
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Eğer anonymous kullanıcı varsa, link et (email/password için linkWithCredential kullan)
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        const oldUserId = auth.currentUser.uid;
        console.log('🔗 Anonymous kullanıcı email/password ile yükseltiliyor, UID:', oldUserId);

        // Credential oluştur
        const credential = EmailAuthProvider.credential(email, password);

        try {
          // linkWithCredential ile anonymous hesabı email/password hesabına bağla
          const result = await linkWithCredential(auth.currentUser, credential);
          const firebaseUser = result.user;

          console.log('✅ UID korundu, yeni Email/Password hesabı bağlandı:', firebaseUser.uid);

          // Firestore'daki user profilini güncelle (anonymous → registered)
          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || null,
            photoURL: firebaseUser.photoURL || null,
            userType: 'registered',
            authProvider: 'email',
            updatedAt: serverTimestamp(),
            // limits'i KALDIR - artık kayıtlı kullanıcı
            limits: deleteField(),
          });

          // User profilini yeniden al
          const userProfile = await getUserProfile(firebaseUser.uid);
          setUser(userProfile);
          await updateLoginTracking(firebaseUser.uid, 'email');
          console.log('✅ Anonymous → Email/Password upgrade başarılı, veriler korundu');
          return;
        } catch (linkError: any) {
          // Eğer hesap zaten varsa (email-already-in-use veya wrong-password), normal giriş yap
          if (linkError.code === 'auth/email-already-in-use' || linkError.code === 'auth/wrong-password') {
            console.log('⚠️ Email zaten kullanımda veya yanlış şifre, normal giriş yapılıyor...');

            // Veri birleştirme yapılmadığı için başarı mesajını engelle
            if (typeof window !== 'undefined') {
              window.localStorage.removeItem('hadAnonymousSession');
              window.localStorage.removeItem('unsentMessage');
              window.localStorage.removeItem('unsentConversationId');
            }

            // Anonymous user'ı sil ve normal giriş yap
            await auth.currentUser.delete();
          } else {
            throw linkError;
          }
        }
      }

      // Normal Email/Password giriş (anonymous değil veya link başarısız)
      const result = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = result.user;

      const userProfile = await getUserProfile(firebaseUser.uid);
      setUser(userProfile);
      await updateLoginTracking(firebaseUser.uid, 'email');
      console.log('✅ Email ile giriş başarılı');
    } catch (err) {
      console.error('Email sign in error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Email ile giriş başarısız';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ==================== Email/Password Sign Up ====================

  const signUpWithEmail = async (
    email: string,
    password: string,
    displayName: string
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const result = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = result.user;

      // Yeni kullanıcı profili oluştur
      const userProfile = await createUserProfile(
        firebaseUser.uid,
        email,
        displayName,
        null,
        'email'
      );

      setUser(userProfile);

      // Track sign up across all analytics
      trackSignUp('email');
      metaPixel.trackSignUp('email');
      eventTracker.startSession(firebaseUser.uid);

      console.log('✅ Email ile kayıt başarılı');
    } catch (err) {
      console.error('Email sign up error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Email ile kayıt başarısız';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ==================== Email Link Sign In (Passwordless) ====================

  const sendEmailLink = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const actionCodeSettings = {
        // Email link'ten sonra yönlendirilecek URL
        url: window.location.origin,
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);

      // Email'i localStorage'a kaydet (verify için gerekli)
      window.localStorage.setItem('emailForSignIn', email);

      console.log('✅ Giriş linki email\'e gönderildi');
    } catch (err) {
      console.error('Send email link error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Email gönderme başarısız';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailLink = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // URL'den email link'i kontrol et
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        throw new Error('Geçersiz email linki');
      }

      // Eğer anonymous kullanıcı varsa, link et
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        const oldUserId = auth.currentUser.uid;
        console.log('🔗 Anonymous kullanıcı email link ile yükseltiliyor, UID:', oldUserId);

        try {
          // Email link credential oluştur
          const credential = EmailAuthProvider.credentialWithLink(email, window.location.href);

          // linkWithCredential ile anonymous hesabı email link hesabına bağla
          const result = await linkWithCredential(auth.currentUser, credential);
          const firebaseUser = result.user;

          console.log('✅ UID korundu, yeni Email Link hesabı bağlandı:', firebaseUser.uid);

          // Firestore'daki user profilini güncelle (anonymous → registered)
          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            email: firebaseUser.email || email,
            displayName: firebaseUser.displayName || null,
            photoURL: firebaseUser.photoURL || null,
            userType: 'registered',
            authProvider: 'email',
            updatedAt: serverTimestamp(),
            // limits'i KALDIR - artık kayıtlı kullanıcı
            limits: deleteField(),
          });

          // User profilini yeniden al
          const userProfile = await getUserProfile(firebaseUser.uid);
          setUser(userProfile);
          await updateLoginTracking(firebaseUser.uid, 'email');
          console.log('✅ Anonymous → Email Link upgrade başarılı, veriler korundu');

          // localStorage'ı temizle
          window.localStorage.removeItem('emailForSignIn');
          return;
        } catch (linkError: any) {
          // Eğer hesap zaten varsa, normal giriş yap
          if (linkError.code === 'auth/email-already-in-use') {
            console.log('⚠️ Email zaten kullanımda, normal giriş yapılıyor...');

            // Veri birleştirme yapılmadığı için başarı mesajını engelle
            if (typeof window !== 'undefined') {
              window.localStorage.removeItem('hadAnonymousSession');
              window.localStorage.removeItem('unsentMessage');
              window.localStorage.removeItem('unsentConversationId');
            }

            // Anonymous user'ı sil ve normal giriş yap
            await auth.currentUser.delete();
          } else {
            throw linkError;
          }
        }
      }

      // Normal Email Link giriş (anonymous değil veya link başarısız)
      const result = await signInWithEmailLink(
        auth,
        email,
        window.location.href
      );
      const firebaseUser = result.user;

      // Firestore'da profil var mı kontrol et
      let userProfile = await getUserProfile(firebaseUser.uid);

      if (!userProfile) {
        // İlk defa giriş yapıyor, profil oluştur
        userProfile = await createUserProfile(
          firebaseUser.uid,
          email,
          null,
          null,
          'email'
        );
      }

      setUser(userProfile);
      await updateLoginTracking(firebaseUser.uid, 'email');

      // localStorage'dan email'i temizle
      window.localStorage.removeItem('emailForSignIn');

      console.log('✅ Email link ile giriş başarılı');
    } catch (err) {
      console.error('Verify email link error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Email doğrulama başarısız';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ==================== Sign Out ====================

  const signOut = async (): Promise<void> => {
    try {
      setLoading(true);
      await firebaseSignOut(auth);
      setUser(null);
      setError(null);
      metaPixel.clearUserData();
      console.log('✅ Çıkış yapıldı');
    } catch (err) {
      console.error('Sign out error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Çıkış yapılamadı';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ==================== Context Value ====================

  const value: AuthContextValue = {
    user,
    loading,
    error,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    sendEmailLink,
    verifyEmailLink,
    signOut,
    createAnonymousUser,
    signInWithApple,
    isGoogleAuthInProgress,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ==================== Custom Hook ====================

/**
 * useAuth hook - AuthContext'i kullanmak için
 * Hata kontrolü ile birlikte gelir
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
